import { useRef, useState } from "react";
import {
  EmailAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor,
  reauthenticateWithCredential,
  sendEmailVerification,
} from "firebase/auth";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { auth } from "@/services/firebase";
import { Button } from "@/components/Button";
import { TextField } from "@/components/Field";

/**
 * Enrolling a second factor.
 *
 * **Before anything else, with no way round it.** This console can read every
 * institution's data and change what any of them is entitled to; the page says
 * that plainly rather than asserting that security is important, because the
 * reason is more persuasive than the rule.
 *
 * SMS rather than an authenticator app because Firebase's multi-factor
 * enrolment supports it directly, and a second factor that exists is worth more
 * than a better one that needs a library and a migration first.
 */
export function EnrolPage() {
  const t = useT();
  const { firebaseUser, signOut } = useAuth();
  const { success, showError, error: notifyError } = useToast();

  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [verificationId, setVerificationId] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  /*
    Firebase refuses to enrol a second factor on an unverified address, and says
    so in a code rather than in words. Somebody whose account was created for
    them in the Firebase console arrives here with exactly that state, and the
    console used to answer with "Something went wrong. It has been logged."
  */
  const [unverified, setUnverified] = useState(false);
  const [sentLink, setSentLink] = useState(false);
  /*
    A session that is real but too old for a sensitive operation.

    Enrolling a second factor is one of the operations Firebase will only do on
    a freshly proven credential. An admin whose sign-in persisted from an
    earlier day — the ordinary case for the first person to enrol — arrives here
    with a valid session whose token predates the enrolment by hours, and the
    `mfaEnrollment:start` call is refused with `auth/requires-recent-login`. The
    console used to answer that, too, with "Something went wrong." The fix is
    not to sign them out and lose the number they typed: it is to ask for the
    password again, refresh the credential in place, and make the attempt the
    stale token refused.
  */
  const [stale, setStale] = useState(false);
  const [password, setPassword] = useState("");
  const recaptcha = useRef<HTMLDivElement>(null);
  /*
    The reCAPTCHA widget is rendered into the div below, and Firebase throws if
    a second one is rendered into an element that already holds it. `send` can
    run more than once — a stale credential fails the first attempt and the
    retry after re-authenticating is the second — so the previous widget is
    cleared before a new verifier is made.
  */
  const verifier = useRef<RecaptchaVerifier | undefined>(undefined);

  const send = async () => {
    if (!firebaseUser || !recaptcha.current) return;
    setBusy(true);
    try {
      const session = await multiFactor(firebaseUser).getSession();
      // Firebase requires a reCAPTCHA before it will send an SMS. Invisible, so
      // the only thing a person sees is the button they pressed.
      verifier.current?.clear();
      verifier.current = new RecaptchaVerifier(auth(), recaptcha.current, { size: "invisible" });
      const id = await new PhoneAuthProvider(auth()).verifyPhoneNumber(
        { phoneNumber: phone.trim(), session },
        verifier.current
      );
      setVerificationId(id);
    } catch (error) {
      const errorCode = (error as { code?: string }).code;
      if (errorCode === "auth/unverified-email") {
        setUnverified(true);
      } else if (errorCode === "auth/requires-recent-login") {
        setStale(true);
      } else {
        showError(error);
      }
    } finally {
      setBusy(false);
    }
  };

  /*
    Prove the password again, then carry on.

    The email is known — this is a signed-in session — so only the password is
    asked for. On success the credential is fresh and `send` is retried with the
    number already typed; a wrong password is named plainly, because there is
    nothing to enumerate to somebody who is already signed in.
  */
  const reauthenticate = async () => {
    if (!firebaseUser?.email || password.length === 0) return;
    setBusy(true);
    try {
      await reauthenticateWithCredential(
        firebaseUser,
        EmailAuthProvider.credential(firebaseUser.email, password)
      );
    } catch (error) {
      const errorCode = (error as { code?: string }).code ?? "";
      if (/wrong-password|invalid-credential|invalid-login/.test(errorCode)) {
        notifyError("mfa.reauth.wrong");
      } else {
        showError(error);
      }
      setBusy(false);
      return;
    }
    setStale(false);
    setPassword("");
    setBusy(false);
    await send();
  };

  const sendVerificationEmail = async () => {
    if (!firebaseUser) return;
    setBusy(true);
    try {
      await sendEmailVerification(firebaseUser);
      setSentLink(true);
      success("mfa.verificationSent");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!firebaseUser || !verificationId) return;
    setBusy(true);
    try {
      const assertion = PhoneMultiFactorGenerator.assertion(
        PhoneAuthProvider.credential(verificationId, code.trim())
      );
      await multiFactor(firebaseUser).enroll(assertion, "Phone");

      /*
        Signed out, on purpose.

        This session was established with a password and nothing else — it is
        the one session that never satisfied the requirement this page exists to
        impose. Carrying it on would mean the first hour after enrolling is the
        least protected hour the account ever has.

        It also removes a whole class of stale-state bug: the enrolled factors
        are cached on the user object Firebase hands out, and a console that
        tried to reason its way to a fresh copy would be arguing with a cache
        on the one screen where being wrong means locking somebody out.
      */
      success("mfa.enrolled");
      await signOut();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-lg px-6 py-16">
      <h1 className="font-display text-2xl text-ink">{t("mfa.title")}</h1>
      <p className="mt-3 text-ink-soft">{t("mfa.body")}</p>

      {unverified ? (
        <div className="mt-8 rounded-md border border-paper-deep bg-paper-warm px-5 py-4">
          <h2 className="font-display text-base text-ink">{t("mfa.unverified.title")}</h2>
          <p className="mt-2 text-sm text-ink-soft">{t("mfa.unverified.body")}</p>
          <div className="mt-4">
            <Button busy={busy} disabled={sentLink} onClick={sendVerificationEmail}>
              {t(sentLink ? "mfa.unverified.sent" : "mfa.unverified.send")}
            </Button>
          </div>
          <p className="mt-3 text-sm text-ink-muted">{t("mfa.unverified.then")}</p>
        </div>
      ) : null}

      {stale ? (
        <div className="mt-8 rounded-md border border-paper-deep bg-paper-warm px-5 py-4">
          <h2 className="font-display text-base text-ink">{t("mfa.reauth.title")}</h2>
          <p className="mt-2 text-sm text-ink-soft">{t("mfa.reauth.body")}</p>
          <div className="mt-4 flex flex-col gap-4">
            <TextField
              labelKey="auth.password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <div>
              <Button busy={busy} disabled={password.length === 0} onClick={reauthenticate}>
                {t("mfa.reauth.confirm")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-8 flex flex-col gap-4">
        <TextField
          labelKey="mfa.phone"
          hintKey="mfa.phoneHint"
          type="tel"
          autoComplete="tel"
          value={phone}
          disabled={verificationId !== undefined}
          onChange={(event) => setPhone(event.target.value)}
        />

        {verificationId === undefined ? (
          <div>
            <Button busy={busy} disabled={phone.trim().length < 6} onClick={send}>
              {t("mfa.send")}
            </Button>
          </div>
        ) : (
          <>
            <TextField
              labelKey="mfa.code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <div>
              <Button busy={busy} disabled={code.trim().length < 4} onClick={verify}>
                {t("mfa.verify")}
              </Button>
            </div>
          </>
        )}
      </div>

      <div ref={recaptcha} />

      <button
        type="button"
        onClick={signOut}
        className="mt-10 text-sm text-ink-muted underline underline-offset-2 hover:text-ink"
      >
        {t("mfa.signOutInstead")}
      </button>
    </main>
  );
}
